<template>
  <Observer name="fade-up" :class="classObs" rootMargin="100px" @enter="enter">
    <div v-if="isFromCustomerService" class="product text-center -no-hover">
      <Observer name="lazy-image" :className="'product-item-image watch no-hover-img'" :rootMargin="'0px'">
        <img :src="product.image + '&width=800'" :alt="product.sku || ' '" loading="lazy" />
      </Observer>
    </div>
    <router-link v-else class="product text-center" :class="{ '-cover': product.imageHover }" :dataSku="product.sku" :to="pdpLink" :aria-label="discoverLabel" @click.native="callGTM('eeListClick')">
      <lazy-image v-if="imageUrl" :class-name="imageUrlHover ? 'product-item-image watch' : 'product-item-image watch no-hover-img'" ref="picture" :item="picture" :sizes="pictureSizes" rootMargin="0px"></lazy-image>
      <lazy-image v-if="imageUrlHover" class-name="product-item-hover-image cover" ref="pictureHover" :item="pictureHover" :sizes="pictureSizes" rootMargin="0px"></lazy-image>
      <span class="text -small" aria-hidden="true" v-if="product.year_of_launch" v-text="product.year_of_launch"></span>
      <h2 class="title -small" aria-hidden="true" v-if="inPLP && product.name" v-text="product.name"></h2>
      <h3 class="title -small" aria-hidden="true" v-if="!inPLP && product.name" v-text="product.name"></h3>
      <div aria-hidden="true" class="cta-container" >
        <div class="btn-wrapper" @click="handleCtaClick">
          <div class="btn -small" aria-hidden="true" v-if="isPreorder">{{ $t('Preorder') }}</div>
          <div class="btn -small" aria-hidden="true" v-else-if="isDiscover && !isBundleInStock">{{ $t('Discover') }}</div>
          <div class="btn -small" aria-hidden="true" v-else>{{ $t('Shop now') }}</div>
        </div>
      </div>
      <span class="text -small" aria-hidden="true" v-if="strapBasePrice(product)">{{ strapBasePrice(product) | currency }}</span>
      <span class="text -small -sold" aria-hidden="true" v-if="isProductSoldOut">{{ $t('Sold') }}</span>
      <span class="text -small -boutique" aria-hidden="true" v-if="isBoutiqueEdition">{{ $t('Boutique Edition') }}</span>
      <span class="text -small -exclusive" aria-hidden="true" v-if="isOnlyOnline">{{ $t('Online exclusive') }}</span>
      <span class="text -small -limited" aria-hidden="true" v-if="!isProductSoldOut && isLimitedEdition">{{ $t('Limited edition') }}</span>
    </router-link>
    <template v-if="isConfig">
      <button type="button" class="btn -stroke -icon -only config-show" :aria-controls="'modal-config-' + listPosition" @click="configProduct = true">
        <i class="icon-cart"></i>
      </button>
      <transition name="fade">
        <div class="config-container" v-if="configProduct">
          <div class="config-modal" :id="'config-modal-' + listPosition">
            <button type="button" class="config-hide" :aria-controls="'config-modal-' + listPosition" @click="configProduct = false">
              <i class="icon-close"></i>
            </button>
            <template v-if="straps && simpleProducts">
              <strong class="strap-title">{{ $t('Wrist size') }}:</strong>
              <ul class="strap-wrists">
                <li v-for="(item, indx) in simpleProducts[STRAP_SIZE_KEY]" :key="`${item.id}-straps`">
                  <button :disabled="!(item.stock && item.stock.is_in_stock)" type="button" @click="strapSize = indx" :class="{ active: strapSize === indx }">
                    <strong>{{ item.option_value_strap_size }}</strong>
                    <span>{{ item.strap_min_length | convertToCM }} - {{ item.strap_max_length | convertToCM }} cm</span>
                  </button>
                </li>
              </ul>
              <a :href="sizingGuidePdf" target="_blank" class="link-light">{{ $t('Sizing guide') }}</a>
              <strong class="strap-title">{{ $t('Choose a buckle') }}:</strong>
              <ul class="strap-buckles">
                <li :key="`no-item-buckle`">
                  <input type="radio" id="no-item-buckle" name="item-buckle-radio" :value="null" checked v-model="buckleType">
                  <label for="no-item-buckle">
                    <span></span>
                    <div :style="'-webkit-box-orient: vertical;'" v-bind:class="{ 'show-lines': buckleType === null }">
                      {{ $t('No Buckle') }}
                    </div>
                  </label>
                </li>
                <li
                  v-for="(item, indx) in simpleProducts[BUCKLE_KEY]"
                  :key="`${item.id}-item-buckle`"
                >
                  <input type="radio" name="item-buckle-radio" :id="`${item.id}-item-buckle`" :value="indx" v-model="buckleType" :disabled="!(item.stock && item.stock.is_in_stock)">
                  <label :for="`${item.id}-item-buckle`">
                    <span></span>
                    <div :style="'-webkit-box-orient: vertical;'" v-bind:class="{ 'show-lines': buckleType === indx }">
                      {{ item.name }}
                    </div>
                  </label>
                </li>
              </ul>
            </template>
            <span class="text -small" aria-hidden="true" v-if="strapPrice">{{ strapPrice | currency }}</span>
            <button type="button" class="btn -icon" @click="addToCartAndFeedback(product)">
              <i class="icon-cart"></i>
              {{ $t('Add to cart') }}
            </button>
          </div>
        </div>
      </transition>
    </template>
  </Observer>
</template>

<script>
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { mapActions } from 'vuex'
import { ReadyHook } from 'vue-asyncready'
import ProductItemData from '../utilities/product-item-data'
import LazyImage from './utils/LazyImage'
import SearchQuery from '@vue-storefront/core/lib/search/searchQuery'
import { quickSearchByQuery } from '@vue-storefront/core/lib/search'
import { STRAP_SIZE_KEY, BUCKLE_KEY, getProductPrice } from '../utilities'
import config from 'config'

export default {
  props: ['product', 'close', 'classObs', 'listPosition', 'listName', 'listType', 'isFromCustomerService', 'inPLP', 'isConfig'],
  name: 'StrapItem',
  mixins: [ReadyHook, ProductItemData],
  components: {
    LazyImage,
  },
  created() {
    this.isEcommerce = this.$store.state['zenith-ui'].ecommerce

    const skus = this.product && this.product.bundle_options ? this.product.bundle_options.map((bundle) => bundle.product_links.map((link) => link.sku)).reduce((acc, curr) => [...acc, ...curr]) : []
    const storeView = currentStoreView()
    const productQuery = ({ skus }) => {
      let query = new SearchQuery().applyFilter({ key: 'sku', value: { in: skus } })
      // .applyFilter({ key: 'visibility', value: { in: [2, 3, 4] } })
      // .applyFilter({ key: 'status', value: { in: [1] } })
      return query
    }
    quickSearchByQuery({ query: productQuery({ skus }), entityType: 'product', storeCode: storeView.storeCode, start: 0, size: 100 })
      .then(({ items }) => items)
      .then((items) => {
        return items.reduce((acc, curr) => {
          if (!acc[curr['attribute_set_id']]) acc[curr['attribute_set_id']] = []
          acc[curr['attribute_set_id']].push(curr)
          return acc
        }, {})
      })
      .then((groups) => {
        this.simpleProducts = groups && groups[STRAP_SIZE_KEY] && groups[BUCKLE_KEY] ? groups : this.simpleProducts
        this.strapSize = groups[STRAP_SIZE_KEY] ? groups[STRAP_SIZE_KEY].findIndex((strap) => strap.option_value_strap_size === 'Standard' && strap.stock && strap.stock.is_in_stock) : null
        this.strapSize = this.strapSize === -1 ? null : this.strapSize
      })

    if (this.strapSize === null) {
      this.strapSize = 0
      this.strapSize = null
    }
  },
  watch: {
    buckleType() {
      const bucklePrice = this.simpleProducts && this.simpleProducts[BUCKLE_KEY] && this.simpleProducts[BUCKLE_KEY][this.buckleType] ? this.simpleProducts[BUCKLE_KEY][this.buckleType].price : 0
      const strapPrice = this.simpleProducts && this.simpleProducts[STRAP_SIZE_KEY] && this.simpleProducts[STRAP_SIZE_KEY][this.strapSize] ? this.simpleProducts[STRAP_SIZE_KEY][this.strapSize].price : 0
      this.strapPrice = bucklePrice + strapPrice
    },
    strapSize() {
      let flag = false
      if (this.strapSize === null) {
        this.strapSize = 0
        flag = true
      }
      const bucklePrice = this.simpleProducts && this.simpleProducts[BUCKLE_KEY] && this.simpleProducts[BUCKLE_KEY][this.buckleType] ? this.simpleProducts[BUCKLE_KEY][this.buckleType].price : 0
      const strapPrice = this.simpleProducts && this.simpleProducts[STRAP_SIZE_KEY] && this.simpleProducts[STRAP_SIZE_KEY][this.strapSize] ? this.simpleProducts[STRAP_SIZE_KEY][this.strapSize].price : 0
      this.strapPrice = bucklePrice + strapPrice
      if (this.strapSize === 0 && flag) this.strapSize = null
    },
  },
  filters: {
    convertToCM: function (value) {
      if (!value) return ''
      return value / 10
    },
  },
  data() {
    return {
      STRAP_SIZE_KEY,
      BUCKLE_KEY,
      simpleProducts: { [STRAP_SIZE_KEY]: [], [BUCKLE_KEY]: [] },
      strapSize: 0,
      buckleType: null,
      strapPrice: 0,
      configProduct: false,
      straps: {
        wrists: [
          {
            size: 'S',
            text: '15 - 20 cm',
          },
          {
            size: 'M',
            text: '18 - 23 cm',
          },
          {
            size: 'L',
            text: '23 - 25 cm',
          },
        ],
        wrist: 1,
        buckles: [
          {
            color: '',
            title: 'Strap only',
          },
          {
            color: 'https://via.placeholder.com/30/FFFF00/',
            title: 'Gold',
          },
          {
            color: 'https://via.placeholder.com/30/333333/',
            title: 'Black Ceramicised Aluminium',
          },
        ],
        buckle: 1,
      },
    }
  },
  computed: {
    isBundleInStock() {
      return this.simpleProducts && this.simpleProducts[STRAP_SIZE_KEY] ? this.simpleProducts[STRAP_SIZE_KEY].filter((product) => product.stock && product.stock.is_in_stock).length >= 1 : false
    },
    pdpLink() {
      return `${this.localizedUrl}/product/${this.product.routeParam ? this.product.routeParam : this.product.url_key}`;
    }
  },
  methods: {
    getProductPrice: getProductPrice,
    ...mapActions('cart', { addToCart: 'addItem' }),
    ...mapActions('feedback', ['show']),
    handleCtaClick(event) {
      if (this.isDiscover && !this.isBundleInStock){
        this.$router.push(this.pdpLink);
      }
      else {
        event.preventDefault();
        this.configProduct = true;
      }
    },
    convertImage(url) {
      return config.magento2.imgUrl + url
    },
    addToCartAndFeedback(product) {
      let ids = []
      let fullProduct = null
      if (!this.size) {
        ids = this.product.bundle_options.map((bundleOption) => {
          let id = null
          let sku = null
          const { option_id: optionId } = bundleOption
          const bundleStrap = bundleOption.product_links.find(({ sku }) => (this.simpleProducts[STRAP_SIZE_KEY] && this.simpleProducts[STRAP_SIZE_KEY][this.strapSize] ? sku === this.simpleProducts[STRAP_SIZE_KEY][this.strapSize].sku : false))
          const bundleBuckle = bundleOption.product_links.find(({ sku }) => (this.simpleProducts[BUCKLE_KEY] && this.simpleProducts[BUCKLE_KEY][this.buckleType] ? sku === this.simpleProducts[BUCKLE_KEY][this.buckleType].sku : false))
          if (bundleStrap) {
            id = bundleStrap.id
            sku = this.simpleProducts[STRAP_SIZE_KEY][this.strapSize].sku
            fullProduct = this.simpleProducts[STRAP_SIZE_KEY][this.strapSize]
            fullProduct.compatibleWatches = this.product.compatible_watches
          }
          if (bundleBuckle) {
            id = bundleBuckle.id
            sku = this.simpleProducts[BUCKLE_KEY][this.buckleType].sku
            fullProduct = this.simpleProducts[BUCKLE_KEY][this.buckleType]
          }
          return {
            optionId,
            id,
            sku,
            fullProduct
          }
        })
      }

      const payload = {
        productToAdd: {
          ...product,
          price: this.simpleProducts[STRAP_SIZE_KEY][this.strapSize].price + (this.buckleType !== null ? this.simpleProducts[BUCKLE_KEY][this.buckleType].price : 0),
          product_option: {
            extension_attributes: {
              bundle_options: ids
                .filter(({ id }) => id !== null)
                .map(({ optionId, id, sku, fullProduct }) => {
                  return {
                    option_id: optionId,
                    option_qty: 1,
                    option_selections: [id],
                    // necessary to check stock status of the simple product of the bundle
                    extension_attributes: {
                      sku,
                      fullProduct
                    }
                  }
                })
            }
          },
          buckleName: this.buckleType !== null ? this.simpleProducts[BUCKLE_KEY][this.buckleType].name : '',
          strapSize: this.simpleProducts[STRAP_SIZE_KEY][this.strapSize].option_value_strap_size,
          sku: product.sku,
          id: product.id,
          image: product.thumbnail,
          name: product.name,
          qty: 1,
        },
      }
      this.addToCart(payload).then(() => {
        this.$bus.$emit('cart-after-item-added', payload)
        /* this.show({
          message: 'Your product has been added to your shopping bag',
        }) */
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

.strap-title{
  font-weight: 700;
}

.cta-container {
  width: 100%;
  position: absolute;
  bottom: 2rem;
  left: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  opacity: 0;
  transition: all 0.4s ease-in-out;

  .btn-wrapper {
    cursor: pointer;
    pointer-events: auto;
  }
}

.obs {
  height: 100%;
}

.strap-buckles {
  flex-wrap: nowrap;
  overflow: auto;
}

.product {
  position: relative;
  background: var(--zenith-component-product-item-background);
  padding: 4rem 2rem 3rem;
  // min-height: 50rem;
  transition: all 0.2s ease-in-out;
  display: flex;
  height: 100%;
  overflow: hidden;
  flex-direction: column;
  backface-visibility: hidden;

  @include bp(max md) {
    padding: 2rem 1rem 2rem;
  }
  @include bp(max sm) {
    padding: 2rem 1rem 2rem;
  }

  .link {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
  }

  span,
  h2,
  h3 {
    position: relative;
    z-index: 20;
    margin-bottom: 0;
    display: block;
    color: var(--zenith-component-product-item-price);
    text-transform: uppercase;
    pointer-events: none;
    transition: all 0.4s ease-in-out;
    &:first-child {
      margin-top: auto;
    }

    &.title {
      color: var(--zenith-component-product-item-title);
      & ~ span {
        margin-top: 1rem;
      }
    }

    &.text {
      & + .title {
        margin-top: 1rem;
      }
    }

    &.-sold,
    &.-boutique,
    &.-exclusive {
      position: absolute;
      top: 2.4rem;
      left: 2rem;
      margin-top: 0 !important;
      max-width: calc(100% - 6.6rem);
      text-align: left;
      & + span.-boutique {
        top: 4rem;
        & + span.-exclusive {
          top: 5.6rem;
        }
      }
      & + span.-exclusive {
        top: 4rem;
      }

      @include bp(max md) {
        top: 1rem;
        left: 1rem;
        max-width: calc(100% - 4rem);
        & + span.-boutique {
          top: 2.8rem;
          & + span.-exclusive {
            top: 4.6rem;
          }
        }
        & + span.-exclusive {
          top: 2.8rem;
        }
      }
    }
  }

  .watch {
    width: 100%;
    height: 100%;
    margin-bottom: 1rem;

    /deep/ img {
      width: 100%;
      max-width: 25rem;
      transition: all 0.6s $ExpoEaseOut;
    }
  }

  .cover {
    position: absolute;
    overflow: hidden;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    opacity: 0;
    transform: translate(-50%, -50%);
    transition: all 0s $ExpoEaseOut;
    pointer-events: none;

    /deep/ img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transform: scale(1.1);
      transition: all 1s $ExpoEaseOut;
    }
  }

  &:not(.-no-hover) {
    &:hover,
    &:active {
      background: var(--zenith-white);
      overflow: visible;

      @include bp(md) {
        .watch {
          /deep/ img {
            transform: scale(1.05);
          }
          &.no-hover-img {
            /deep/ img {
              transform: scale(1.05);
            }
          }
        }
      }

      .cta-container {
        opacity: 1;
      }

      .cover {
        opacity: 1;
        /deep/ img {
          transform: scale(1);
        }
      }
      & + .close {
        opacity: 1;
        & + .fav {
          opacity: 0;
        }
      }
      .title,
      .text {
        opacity: 0;
      }
    }

    &:focus {
      @include bp(md) {
        .watch {
          /deep/ img {
            transform: scale(1.05);
          }
          &.no-hover-img {
            /deep/ img {
              transform: scale(1.05);
            }
          }
        }
      }
    }
  }

  &.-cover {
    &:hover,
    &:hover:focus,
    &:active {
      & + .fav,
      & + .fav-full,
      & + .close {
        .icon-fav {
          color: var(--zenith-component-title);
          &-full {
            color: var(--zenith-component-title);
          }
        }
        &:hover {
          .icon-fav {
            color: var(--zenith-component-product-item-heart-hover-with-cover);
            &-full {
              color: var(--zenith-component-product-item-heart-hover-with-cover);
            }
          }
        }

        span {
          &.icon-anim {
            i {
              color: var(--zenith-component-product-item-heart-anim);
            }
          }
        }
      }
      .btn {
        pointer-events: none;
      }
    }
  }
}

.fav,
.close {
  position: absolute;
  top: 2rem;
  right: 2rem;
  z-index: 40;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  transition: all 0.2s ease-in-out;

  @include bp(max md) {
    top: 0.7rem;
    right: 0.7rem;
  }
}

.close {
  opacity: 0;

  &:hover,
  &:focus,
  &:active {
    transform: scale(1.1);
  }

  i {
    display: block;
    color: var(--zenith-component-product-item-like);
  }
}

.fav {
  i {
    display: block;
    &.icon-fav {
      color: var(--zenith-component-product-item-like);

      &-full {
        color: var(--zenith-component-product-item-like);
      }
    }
  }

  span {
    &.icon-anim {
      position: absolute;
      top: 2%;
      left: 8%;
      opacity: 0;
      pointer-events: none;
      display: inline-block;
      font-size: 12px;
      z-index: -1;

      i {
        color: var(--zenith-component-product-item-heart-anim);
      }
    }
  }

  &:hover,
  &:focus {
    i {
      transition: transform 0.4s cubic-bezier(0.075, 0.82, 0.165, 1);
      transform: scale(1.2);
      &.icon-fav {
        color: var(--zenith-component-product-item-heart-hover);
        &:before {
          content: '\e93d';
        }

        &-full {
          color: var(--zenith-component-product-item-heart-hover);
        }
      }
    }
    span {
      &.icon-anim {
        i {
          color: var(--zenith-component-product-item-heart-anim);
        }
      }
    }
  }

  &.active {
    i {
      display: block;
      animation: beat cubic-bezier(0.04, 0.4, 0.5, 0.95) 1.2s forwards 1;
    }
    span {
      &.icon-anim {
        animation: floatAway ease-out 2s forwards 1;
      }
    }
  }
}

.config {
  &-container{
    position: absolute;
    top: 0;
    left: 0;
    z-index: 200;
    height: 100%;
    width: 100%;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 2px;
      height: 2px;
      border-radius: 2px;
      background-color: #DFDFDF;
    }

    &::-webkit-scrollbar-thumb {
      background: #262626;
      border-radius: 2px;
    }
  }

  &-show {
    position: absolute;
    top: 2rem;
    right: 2rem;
    z-index: 50;
  }

  &-hide {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 250;
    border: none;
    background: none;
    font-size: em(20);
    padding: 1rem;

    i {
      display: block;
    }
  }

  &-modal {
    display: flex;
    text-align: left;
    flex-direction: column;
    min-height: 100%;
    padding: 4rem 2rem 0;
    background: var(--zenith-component-product-item-background);

    .btn.-icon {
      margin: auto -2rem 0 -2rem;
      &:after {
        border-radius: 0;
      }
    }

    .link-light {
      margin-right: auto;
      margin-bottom: 2rem;
    }

    .title {
      text-align: center;
      margin-bottom: 0.6rem;

      &:nth-last-child(2) {
        margin-bottom: 2rem;
      }
    }

    .text {
      display: block;
      text-align: center;
      margin-bottom: 2rem;
      color: var(--zenith-component-product-item-price);
    }
  }
}
</style>
